import type { StreamEvent } from '@vibeshell/shared';

/**
 * Error-recovery wrapper around the SSE stream.
 *
 * Strategy:
 *   1. The SSE call drops. Reconnect up to 2 times with exponential backoff
 *      (1s, then 2s). The session id is reused so the worker can resume from
 *      cache where possible.
 *   2. Both retries fail → surface a final, user-actionable error:
 *      "Connection lost. Your work is saved — refresh to continue."
 *
 * Previously this layer had a third stage: poll
 * `/v1/generate/:sessionId/status` for ~6 seconds before giving up. That
 * endpoint never existed in the gateway, so the polling stage wasted
 * 6 seconds of dead time before surfacing the connection-lost UX. The
 * stage was removed in NIGHT_AUDIT §2.5; restoring it requires server
 * support (a session-keyed last-event cache in KV).
 *
 * The helper is DOM-agnostic: no React/Preact, no `window` access — it just
 * coordinates `fetch` calls and timers and forwards events to a callback.
 */

const MAX_SSE_RETRIES = 2;
const BACKOFF_MS = [1000, 2000];
export const CONNECTION_LOST_MESSAGE =
  'Connection lost. Your work is saved. Refresh to continue.';

export interface RecoverableStreamParams {
  sessionId: string;
  onEvent: (event: StreamEvent) => void;
  onFatal: (message: string) => void;
  /**
   * Async callback that opens the SSE stream. It must call back into the
   * `onEvent`/`onError`/`onClose` plumbing the wrapper provides. Returning
   * an AbortController allows the wrapper to cancel in-flight requests.
   */
  openStream: (handlers: {
    onEvent: (event: StreamEvent) => void;
    onClose: () => void;
    onError: (err: unknown) => void;
  }) => Promise<AbortController>;
}

export class RecoverableStream {
  private retries = 0;
  private aborted = false;
  private current: AbortController | null = null;
  private backoffTimer: ReturnType<typeof setTimeout> | null = null;
  private settled = false;
  private readonly params: RecoverableStreamParams;

  constructor(params: RecoverableStreamParams) {
    this.params = params;
  }

  async start(): Promise<void> {
    await this.openOnce();
  }

  abort(): void {
    this.aborted = true;
    this.current?.abort();
    if (this.backoffTimer) clearTimeout(this.backoffTimer);
    this.backoffTimer = null;
  }

  private async openOnce(): Promise<void> {
    if (this.aborted || this.settled) return;
    try {
      this.current = await this.params.openStream({
        onEvent: (event) => {
          // Any successful event resets the retry budget so a single hiccup
          // mid-stream doesn't burn through both retries.
          this.retries = 0;
          // Mark the stream TERMINAL on any of:
          //   • deploy_complete — the canonical success signal.
          //   • error event with retryable === false — application-level
          //     terminal failures: 429 rate-limit, 403 GUEST_LIMIT_REACHED,
          //     prompt-too-long. Phase 9 fix — without this, sse.ts would
          //     fire the error event + onClose, and onClose would trigger
          //     handleFailure() → retry. That retry hit the same 403 again
          //     and refired the AuthWall, cascading into the "auth wall
          //     flashing repeatedly" symptom the user reported on mobile.
          //
          // `stream_close` deliberately does NOT settle here — it's
          // transport-level and historically fired on premature drops too.
          if (event.type === 'deploy_complete') {
            this.settled = true;
          } else if (event.type === 'error' && event.retryable === false) {
            this.settled = true;
          }
          this.params.onEvent(event);
        },
        onClose: () => {
          // Clean close. If we never saw a settling event the stream ended
          // prematurely — escalate to a retry.
          if (!this.settled && !this.aborted) {
            void this.handleFailure();
          }
        },
        onError: () => {
          if (!this.aborted) void this.handleFailure();
        }
      });
    } catch {
      if (!this.aborted) void this.handleFailure();
    }
  }

  private async handleFailure(): Promise<void> {
    if (this.aborted || this.settled) return;
    if (this.retries < MAX_SSE_RETRIES) {
      const wait = BACKOFF_MS[this.retries] ?? BACKOFF_MS[BACKOFF_MS.length - 1];
      this.retries++;
      this.backoffTimer = setTimeout(() => {
        this.backoffTimer = null;
        void this.openOnce();
      }, wait);
      return;
    }
    // SSE retries exhausted. Previously this fell through to a polling
    // stage against a nonexistent endpoint; that has been removed (see
    // file header). Surface the connection-lost UX immediately.
    this.fail(CONNECTION_LOST_MESSAGE);
  }

  private fail(message: string): void {
    if (this.settled) return;
    this.settled = true;
    this.params.onFatal(message);
  }
}
